// The Skill Builder KPI strip on the REPORT.
//
// Six cards, asked for by name: Total, Assigned, Completed, Incomplete, and the
// two halves of Completed split on the pass mark.
//
// Colour-coded to match BmdReportKpiCards on the Main Report, and using that
// exact palette, so the strips do not read as different kinds of thing on
// adjacent tabs. The add-activity page keeps a plain strip
// (BmdSkillbuilderStats): colour is for the report, where this is the headline
// of the page rather than a caption above a picker.
//
// How the six relate, which is worth knowing before changing any of them:
//
//   Total       = Completed + Incomplete
//   Completed   = Above the pass mark + Below it
//   Assigned    is work handed over and NOT yet submitted, so it is a subset of
//               Incomplete rather than a seventh slice. It overlaps on purpose:
//               a tutor wants "how much is out with the student" as well as
//               "how much is left".
//
// All six come off one `counts` object computed server-side, so nothing here
// does arithmetic that another surface might do differently.

import React from "react"
import { Col, Row } from "reactstrap"
import type { BmdSkillbuilderCounts } from "../types/bmd-skillbuilder.types"

type Props = {
  counts?: BmdSkillbuilderCounts | null
  passPercent: number
  className?: string
}

type Tone = "slate" | "purple" | "green" | "amber" | "teal" | "red"

// The five in BmdReportKpiCards, plus a slate for the total and a teal for the
// passing half, which that strip has no equivalent of.
const TONES: Record<Tone, { bg: string; value: string }> = {
  slate: { bg: "#e8eaf0", value: "#334155" },
  purple: { bg: "#ede9fe", value: "#6d28d9" },
  green: { bg: "#d1fae5", value: "#047857" },
  amber: { bg: "#fff4d6", value: "#92400e" },
  teal: { bg: "#dbeafe", value: "#1d4ed8" },
  red: { bg: "#fde2e1", value: "#b42318" },
}

type CardProps = {
  label: string
  value: number
  hint?: string
  tone: Tone
}

const KpiCard = ({ label, value, hint, tone }: CardProps) => {
  const t = TONES[tone]
  return (
    <Col xs={6} md={4} lg={2} className="mb-2">
      <div className="rounded p-3 h-100" style={{ backgroundColor: t.bg }}>
        <div className="text-uppercase fw-bold font-size-12 text-muted">
          {label}
        </div>
        <div className="fw-bold" style={{ fontSize: "28px", color: t.value }}>
          {value}
        </div>
        {/* Held even when empty so the six cards keep a common height rather
            than stepping up and down as a student's counts change. */}
        <div className="font-size-12 text-muted">{hint || " "}</div>
      </div>
    </Col>
  )
}

export function BmdSkillbuilderKpiCards({
  counts,
  passPercent,
  className,
}: Props) {
  return (
    <Row className={`g-2 mb-3 ${className || ""}`}>
      {/* Worksheets, not failed skills: the two differ only where a skill has
          no worksheet authored yet, and a card headed "Total skill builders"
          has to be the number of skill builders. */}
      <KpiCard
        label="Total Skill Builders"
        tone="slate"
        value={counts?.with_skillbuilder ?? 0}
      />
      <KpiCard label="Assigned" tone="purple" value={counts?.assigned ?? 0} />
      <KpiCard label="Completed" tone="green" value={counts?.completed ?? 0} />
      <KpiCard
        label="Incomplete"
        tone="amber"
        value={counts?.incomplete ?? 0}
        hint="Not finished yet"
      />
      <KpiCard
        label={`Scored ${passPercent}% or above`}
        tone="teal"
        value={counts?.above_pass ?? 0}
      />
      <KpiCard
        label={`Scored under ${passPercent}%`}
        tone="red"
        value={counts?.needs_tutor ?? 0}
        hint="Needs a tutor"
      />
    </Row>
  )
}

export default BmdSkillbuilderKpiCards
